/**
 * Centralized error reporting utility.
 *
 * All caught errors should go through this function instead of raw console.error.
 * Reports to Sentry when initialised, always logs to console for local debugging.
 *
 * Usage:
 *   import { reportError } from '@paulownia/shared';
 *   reportError(error, { context: 'loadTenantSettings', tenantId });
 */
import * as Sentry from '@sentry/react';

export function reportError(error: unknown, context?: Record<string, unknown>): void {
  // Always log to console for local debugging
  const errorMessage = error instanceof Error ? error.message : String(error);
  if (context) {
    console.error(`[Error] [${context.context || 'unknown'}] ${errorMessage}`, context, error);
  } else {
    console.error(`[Error] ${errorMessage}`, error);
  }

  // Send to Sentry (no-op if Sentry isn't initialised)
  Sentry.captureException(error, { extra: context });
}

/**
 * Lightweight warning reporter for non-critical issues.
 * Use for expected failures (e.g. storage unavailable, optional feature degradation).
 */
export function reportWarning(message: string, context?: Record<string, unknown>): void {
  if (context) {
    console.warn('[Warning]', message, context);
  } else {
    console.warn('[Warning]', message);
  }

  Sentry.captureMessage(message, { level: 'warning', extra: context });
}
